/**
 * TypeScript mirrors of the DTOs in IdVerify.Portal.Api/Models.
 * ASP.NET Core serialises with camelCase by default, so property names line up 1:1
 * with the C# class members. Keep the two in sync when either side changes.
 */

// -- Auth ---------------------------------------------------------------------

export interface CurrentUser {
  id: number;
  fullName: string;
  email: string;
  /** "Viewer" | "Operator" | "ClientAdmin" | "SuperAdmin" */
  role: string;
  roleDisplayName: string;
  clientId: number | null;
  clientName: string | null;
  /** "Active" | "Suspended" | "Deactivated" - drives the My Company sidebar badge. Null for SuperAdmin. */
  clientStatus?: string | null;
  isSuperAdmin: boolean;
  isClientAdmin: boolean;

  /**
   * True while the account is on a password somebody else chose - emailed at sign-up or after an
   * administrator reset. The app routes such a session to the change-password screen and refuses
   * to render anything else until it clears.
   */
  mustChangePassword: boolean;

  emailVerified: boolean;
  /** Two-step sign-in is on. */
  twoFactorEnabled: boolean;
  /** How codes are sent while on; null when off. */
  twoFactorMethod: TwoFactorMethod | null;
  /** Where codes come from while on: "•••• 4567", "j•••@acme.com" or "your authenticator app". */
  twoFactorSentTo: string | null;
  /** The account address, masked - where email codes would go. */
  emailHint: string;
  /** The platform requires two-step sign-in for everyone; it cannot be turned off. */
  twoFactorRequired: boolean;
  /**
   * Required and not set up yet: the app routes this session to My Profile and refuses to render
   * anything else until it clears, as with `mustChangePassword`.
   */
  twoFactorSetupRequired: boolean;
}

/** Where a two-step sign-in code comes from. */
export type TwoFactorMethod = 'sms' | 'email' | 'authenticator';

/** POST /api/auth/two-factor/authenticator/start - what the user needs to add the app. */
export interface AuthenticatorSetupResponse {
  secret: string;
  /** The key in groups of four, for typing in by hand. */
  formattedSecret: string;
  otpAuthUri: string;
  /** data:image/svg+xml;base64,... */
  qrCodeDataUri: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface LoginResponse {
  accessToken: string;
  /** ISO 8601 UTC instant. */
  expiresAt: string;
  user: CurrentUser;
}

/** POST /api/auth/login when two-step sign-in is on: the password was right, now the code. */
export interface TwoFactorChallengeResponse {
  requiresTwoFactor: true;
  /** Opaque; sent back with the code. Valid for 10 minutes. */
  challenge: string;
  /** Whether the code was texted, emailed, or comes from the authenticator app. */
  method: TwoFactorMethod;
  /** Masked destination: "•••• 4567", "j•••@acme.com" or "your authenticator app". */
  sentTo: string;
  message?: string | null;
}

export type LoginResult = LoginResponse | TwoFactorChallengeResponse;

export function isTwoFactorChallenge(r: LoginResult): r is TwoFactorChallengeResponse {
  return (r as TwoFactorChallengeResponse).requiresTwoFactor === true;
}

export interface ApiErrorBody {
  message: string;
  errors?: Record<string, string[]>;
}

// -- Dashboard ----------------------------------------------------------------

export interface DailyVerificationStat {
  date: string;
  approved: number;
  declined: number;
  pending: number;
}

export interface VerificationByTypeItem {
  type: string;
  count: number;
}

export interface RecentVerificationItem {
  id: number;
  /** The name, or a stand-in before the ID is read (ApplicantLabel, Core). */
  applicantName: string;
  /** False when applicantName is a stand-in; shown muted. */
  applicantIsName: boolean;
  clientName: string;
  status: string;
  type: string;
  confidence: number;
  createdAt: string;
}

export interface TopClientItem {
  id: number;
  companyName: string;
  verificationCount: number;
  approvalRate: number;
}

export interface DashboardResponse {
  totalVerifications: number;
  approvedCount: number;
  declinedCount: number;
  /** Completed verifications whose decision a reviewer changed. */
  decisionsChangedCount: number;
  activeClients: number;
  openFraudAlerts: number;
  approvalRate: number;
  averageConfidence: number;
  dailyStats: DailyVerificationStat[];
  byType: VerificationByTypeItem[];
  recentVerifications: RecentVerificationItem[];
  topClients: TopClientItem[];
}

// -- Verifications ------------------------------------------------------------

export interface VerificationListItem {
  id: number;
  sessionToken: string;
  /** The name, or a stand-in before the ID is read (ApplicantLabel, Core). */
  applicantName: string;
  /** False when applicantName is a stand-in; shown muted. */
  applicantIsName: boolean;
  clientName: string;
  verificationType: string;
  status: string;
  overallConfidence: number;
  /** Only the back was scanned (ExtractionMethod BarcodeOnly): confidence shows N/A. */
  barcodeOnly: boolean;
  riskScore: number;
  createdAt: string;
  completedAt: string | null;
}

export interface VerificationListResponse {
  sessions: VerificationListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  statusFilter: string | null;
  typeFilter: string | null;
  searchTerm: string | null;
  totalPages: number;
}

export interface DocumentImageItem {
  /** Opaque public id (GUID). The internal primary key is never sent to the client. */
  externalId: string;
  imageType: string;
}

export interface BarcodeDataItem {
  documentNumber: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  expirationDate: string | null;
  issueDate: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  sex: string | null;
  height: string | null;
  eyeColor: string | null;
  hairColor: string | null;
  vehicleClass: string | null;
  restrictions: string | null;
  endorsements: string | null;
  aamvaVersion: number | null;
}

/** A stored MRZ as the verification page shows it (MrzSummary in Core). Dates are ISO. */
export interface MrzSummary {
  lines: string[];
  /** "TD1" | "TD2" | "TD3"; null when the zone does not read (malformed or not genuine). */
  format: string | null;
  documentCode: string | null;
  issuingState: string | null;
  surname: string | null;
  givenNames: string | null;
  documentNumber: string | null;
  nationality: string | null;
  dateOfBirth: string | null;
  sex: string | null;
  expirationDate: string | null;
  optionalData: string | null;
  checks: { name: string; passed: boolean }[];
  valid: boolean;
}

export interface DocumentDetailItem {
  /** The machine-readable zone (passports, MRZ cards); null when there is none. */
  mrz: MrzSummary | null;
  id: number;
  documentType: string;
  issuingCountry: string | null;
  issuingState: string | null;
  documentNumber: string | null;
  fullName: string | null;
  dateOfBirth: string | null;
  expirationDate: string | null;
  issueDate: string | null;
  address: string | null;
  extractionMethod: string;
  ocrConfidence: number;
  overallConfidence: number;
  verificationStatus: string;
  images: DocumentImageItem[];
  barcodeData: BarcodeDataItem | null;
  /** Diagnostics below are populated for SuperAdmins only. */
  rawOcrJsonBytes: number;
  rawOcrPreview: string | null;
  rawOcrTruncated: boolean;
  unparsedFieldCount: number;
  unparsedFields: string[];
  trackedFieldCount: number;
}

export interface FaceCaptureItem {
  id: number;
  livenessCheckPassed: boolean;
  livenessScore: number;
  faceMatchScore: number;
  faceMatchPassed: boolean;
  /** The matcher that decided, e.g. "neural" (face-api). */
  faceMatchMethod: string | null;
  /** SuperAdmin only — the matcher's diagnostic flags (e.g. "multiple-id-faces"); empty otherwise. */
  faceMatchFlags: string[];
  /**
   * TEST DATA, SuperAdmin only — a candidate model's score ("sface", raw cosine -1 to 1) for the
   * same faces. Never used in the decision; null for other roles and older captures.
   */
  shadowFaceMatchMethod: string | null;
  shadowFaceMatchScore: number | null;
  createdAt: string;
  /** One entry per requested liveness challenge; empty for captures recorded before these were stored. */
  livenessChallenges: LivenessChallengeItem[];
}

export interface LivenessChallengeItem {
  /** "center" | "left" | "right" | "up" | "blink" | "flash" | "smile" */
  challenge: string;
  /** "unverified" = the device claimed it but asserted an overall liveness failure. */
  status: 'passed' | 'failed' | 'unverified' | 'not-completed';
  /** "server" = IdVerify verified it; "device" = reported by the customer's phone. */
  verifiedBy: 'server' | 'device';
  score: number | null;
  detail: string | null;
}

export interface VerificationResultItem {
  decision: string;
  overallConfidence: number;
  riskScore: number;
  documentVerified: boolean;
  faceMatchVerified: boolean;
  livenessVerified: boolean;
  manualReviewNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  fraudSignals: string[];
}

export interface FraudAlertItem {
  id: number;
  alertType: string;
  severity: string;
  description: string;
  confidenceScore: number;
  status: string;
  createdAt: string;
}

export interface VerificationDetailResponse {
  id: number;
  /** False when the session had no face-match step: no comparison ran, so the scores and pass flags are not results. */
  faceMatchRequested: boolean;
  /** False when the session had no liveness step: the result's livenessVerified is a default. */
  livenessRequested: boolean;
  sessionToken: string;
  applicantFirstName: string;
  applicantLastName: string;
  applicantEmail: string | null;
  applicantPhone: string | null;
  externalReferenceId: string | null;
  clientName: string;
  clientId: number;
  verificationType: string;
  status: string;
  overallConfidence: number;
  riskScore: number;
  decisionReason: string | null;
  ipAddress: string | null;
  country: string | null;
  address: string | null;
  createdAt: string;
  completedAt: string | null;
  documents: DocumentDetailItem[];
  faceCaptures: FaceCaptureItem[];
  result: VerificationResultItem | null;
  fraudAlerts: FraudAlertItem[];
}

export interface ReviewDecisionRequest {
  newStatus: string;
  decisionReason: string | null;
}

// -- Documents ----------------------------------------------------------------

export interface DocumentListItem {
  id: number;
  verificationSessionId: number;
  applicantName: string;
  documentType: string;
  issuingCountry: string | null;
  documentNumber: string | null;
  extractionMethod: string;
  overallConfidence: number;
  verificationStatus: string;
  createdAt: string;
}

export interface DocumentListResponse {
  documents: DocumentListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  typeFilter: string | null;
  statusFilter: string | null;
  totalPages: number;
}

// -- Fraud --------------------------------------------------------------------

export interface FraudAlertListItem {
  id: number;
  verificationSessionId: number;
  applicantName: string;
  alertType: string;
  severity: string;
  description: string;
  confidenceScore: number;
  status: string;
  createdAt: string;
  /** A name for SuperAdmins, "InertiaID team" for client users; null while unresolved. */
  resolvedBy: string | null;
  resolvedAt: string | null;
}

export interface FraudAlertDetail extends FraudAlertListItem {
  resolutionNotes: string | null;
  code: string | null;
  evidence: Record<string, string | null>;
  /** Status changes, oldest first. SuperAdmin only; empty for client users. */
  history: { at: string; byUser: string | null; details: string }[];
  /** True for SuperAdmin, the only role that changes alerts. */
  canManage: boolean;
  allowedStatuses: { value: string; label: string; notesRequired: boolean }[];
}

export interface FraudAlertStatusRequest {
  status: string;
  notes: string | null;
}

export interface FraudByTypeItem {
  type: string;
  count: number;
}

export interface DailyFraudStat {
  date: string;
  count: number;
}

export interface FraudDashboardResponse {
  totalAlerts: number;
  openAlerts: number;
  criticalAlerts: number;
  resolvedThisWeek: number;
  recentAlerts: FraudAlertListItem[];
  alertsByType: FraudByTypeItem[];
  dailyStats: DailyFraudStat[];
}

export interface FraudAlertListResponse {
  alerts: FraudAlertListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  severityFilter: string | null;
  statusFilter: string | null;
  totalPages: number;
}

// -- Audit logs ---------------------------------------------------------------

export interface AuditLogListItem {
  id: number;
  action: string;
  entityType: string;
  entityId: number | null;
  userName: string | null;
  ipAddress: string | null;
  severity: string;
  details: string | null;
  createdAt: string;
}

/**
 * One line on a verification's Event timeline (Core VerificationEvent). Newest first from the API.
 * `detail` is a short second line; `logId` links it to that call in API Logs.
 */
export interface VerificationEvent {
  at: string;
  /** session | step | ocr | decision | fraud | webhook | api */
  category: string;
  /** success | danger | warning | neutral */
  tone: string;
  title: string;
  detail: string | null;
  logId: number | null;
}

/** One call to the public API, as the API Logs list shows it (Core ApiRequestLogItem). */
export interface ApiLogItem {
  id: number;
  createdAt: string;
  /** "api" (a call to the API) or "webhook" (a delivery to the client; path is its URL). */
  kind: string;
  /** A webhook's event, e.g. "verification.decided". */
  event: string | null;
  clientId: number | null;
  clientName: string | null;
  environment: string | null;
  keyPrefix: string | null;
  method: string;
  path: string;
  statusCode: number;
  errorCode: string | null;
  durationMs: number;
  verificationSessionId: number | null;
  ipAddress: string | null;
  requestId: string;
}

export interface ApiLogPage {
  items: ApiLogItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** One call in full; the redacted bodies are null unless bodiesVisible (ClientAdmin / SuperAdmin). */
export interface ApiLogDetail {
  summary: ApiLogItem;
  userAgent: string | null;
  requestBytes: number;
  responseBytes: number;
  bodiesVisible: boolean;
  requestBody: string | null;
  responseBody: string | null;
}

export interface AuditLogListResponse {
  logs: AuditLogListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  severityFilter: string | null;
  entityTypeFilter: string | null;
  searchTerm: string | null;
  totalPages: number;
}

// -- Reports ------------------------------------------------------------------

export interface MonthlyTrendItem {
  month: string;
  total: number;
  approved: number;
  declined: number;
}

export interface ConfidenceDistributionItem {
  range: string;
  count: number;
}

export interface AnalyticsResponse {
  monthlyTrends: MonthlyTrendItem[];
  byVerificationType: VerificationByTypeItem[];
  confidenceDistribution: ConfidenceDistributionItem[];
  /** Already scaled to 0-100 by the API. */
  averageRiskScore: number;
  /** Already scaled to 0-100 by the API. */
  averageConfidence: number;
}

export interface ComplianceByCountryItem {
  country: string;
  count: number;
  approvalRate: number;
}

export interface ComplianceByDocTypeItem {
  documentType: string;
  count: number;
  averageConfidence: number;
}

export interface ComplianceResponse {
  startDate: string;
  endDate: string;
  totalVerifications: number;
  approvedCount: number;
  declinedCount: number;
  /** Completed verifications whose decision a reviewer changed. */
  decisionsChangedCount: number;
  approvalRate: number;
  fraudAlertsRaised: number;
  fraudAlertsResolved: number;
  byCountry: ComplianceByCountryItem[];
  byDocumentType: ComplianceByDocTypeItem[];
}

// -- Users --------------------------------------------------------------------

export interface ClientSelectItem {
  id: number;
  clientName: string;
}

export interface UserListItem {
  id: number;
  fullName: string;
  email: string;
  role: string;
  roleDisplayName: string;
  isActive: boolean;
  clientId: number | null;
  clientName: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface UserListResponse {
  users: UserListItem[];
  clientSelectItems: ClientSelectItem[];
}

export interface UserProfileResponse {
  id: number;
  fullName: string;
  email: string;
  role: string;
  roleDisplayName: string;
  clientId: number | null;
  clientName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /** On a temporary password, and must choose their own at next sign-in. */
  mustChangePassword: boolean;
  /** Null for accounts that predate password-change tracking. */
  passwordChangedAt: string | null;
}

export interface CreateUserRequest {
  fullName: string;
  email: string;
  role: string;
  clientId: number | null;
}

export interface UpdateUserRequest {
  fullName: string;
  email: string;
  role: string;
  isActive: boolean;
}

export interface ToggleUserResponse {
  id: number;
  isActive: boolean;
}

// -- Demo extraction ----------------------------------------------------------

export interface FieldSource {
  type: string;
  value: string;
  confidence: number;
}

export interface ExtractedField {
  value: string;
  confidence: number;
  sources: FieldSource[];
}

export interface MrzResponse {
  line1: string | null;
  line2: string | null;
}

export interface MergedFields {
  firstName?: ExtractedField | null;
  middleName?: ExtractedField | null;
  lastName?: ExtractedField | null;
  dateOfBirth?: ExtractedField | null;
  street?: ExtractedField | null;
  city?: ExtractedField | null;
  state?: ExtractedField | null;
  postalCode?: ExtractedField | null;
  gender?: ExtractedField | null;
  nationality?: ExtractedField | null;
  documentType?: ExtractedField | null;
  documentNumber?: ExtractedField | null;
  documentDiscriminator?: ExtractedField | null;
  country?: ExtractedField | null;
  issueDate?: ExtractedField | null;
  expirationDate?: ExtractedField | null;
  class?: ExtractedField | null;
  height?: ExtractedField | null;
  weight?: ExtractedField | null;
  eyeColor?: ExtractedField | null;
  hairColor?: ExtractedField | null;
  restrictions?: ExtractedField | null;
  endorsements?: ExtractedField | null;
  aamvaVersion?: number | null;
  mrz?: MrzResponse | null;
}

export interface ConfidenceScores {
  ocr: number;
  barcode: number;
  fieldConsistency: number;
  overall: number;
}

export interface DemoExtractResponse {
  status: string;
  fields: MergedFields;
  confidence: ConfidenceScores;
  processingTimeMs: number;
  hasBarcodeData: boolean;
  rawJson: string | null;
}

/** Today's Try Extractor allowance. `limited` is false for platform administrators. */
export interface DemoUsageResponse {
  limited: boolean;
  usedToday: number;
  dailyLimit: number;
  remaining: number;
}

// -- Billing / credits ---------------------------------------------------------

export interface CreditSummary {
  clientId: number;
  clientName: string;
  /** Wallet balance in USD. */
  balance: number;
  freeTransactionsRemaining: number;
  /** This client's price per call for each service. */
  rates: ServicePrices;
  /** The ID verification rate (what the estimate below counts in). */
  currentRate: number;
  /** Roughly how many ID verifications the balance buys. */
  estimatedVerificationsRemaining: number;
  /** Out of credit: the API is blocked until they top up. */
  isExhausted: boolean;
  /** Below the low-balance warning threshold, but still usable. */
  isLow: boolean;
  isPrepaid: boolean;
  /** A card is saved. Without one, top-ups cannot run. */
  hasPaymentMethod: boolean;
  autoTopUpEnabled: boolean;
  autoTopUpAmount: number;
  autoTopUpThreshold: number;
  /** Consecutive failed automatic top-ups - non-zero means the card needs attention. */
  failedTopUpAttempts: number;
  minimumPurchase: number;
  maximumPurchase: number;
  defaultAutoTopUpAmount: number;
}

export interface CreditLedgerEntry {
  id: number;
  createdAt: string;
  /** "Purchase", "Auto Top-Up", "Verification", "Trial Grant", ... */
  type: string;
  /** Signed: negative for consumption. */
  amount: number;
  balanceAfter: number;
  description: string;
  verificationSessionId: number | null;
  createdByEmail: string | null;
  isCredit: boolean;
}

export interface CreditPageResponse {
  summary: CreditSummary;
  ledger: CreditLedgerEntry[];
}

export interface PurchaseCreditsRequest {
  amount: number;
}

export interface AutoTopUpRequest {
  enabled: boolean;
  amount: number;
  threshold: number;
}

export interface CreditOperationResponse {
  success: boolean;
  error: string | null;
  newBalance: number;
}

export interface InvoiceListItem {
  id: number;
  createdAt: string;
  chargeType: string;
  periodLabel: string;
  transactionCount: number;
  grossAmount: number;
  creditApplied: number;
  netAmount: number;
  status: string;
  stripePaymentIntentId: string | null;
  paidAt: string | null;
  failureReason: string | null;
}

// -- Company / account ---------------------------------------------------------

export interface CardOnFile {
  last4: string;
  brand: string;
  expiryMonth: number | null;
  expiryYear: number | null;
}

export interface CompanyResponse {
  clientId: number;
  clientName: string;
  contactEmail: string;
  contactPhone: string;
  industry: string;
  tier: string;
  status: string;
  /** "TestMode" | "Prepaid" | "CreditExhausted" - the whole enum. */
  billingStatus: string;
  isTestMode: boolean;
  isPrepaid: boolean;
  planName: string | null;
  rateLimitPerMinute: number;
  prepaidCreditBalance: number;
  freeTransactionsRemaining: number;
  currentMonthTransactions: number;
  currentMonthCost: number;
  billingPeriodStart: string;
  autoTopUpEnabled: boolean;
  autoTopUpAmount: number;
  autoTopUpThreshold: number;
  card: CardOnFile | null;
  /** Test Mode only: verifications spent today and the daily allowance. Both 0 in Production. */
  testModeUsedToday: number;
  testModeDailyLimit: number;
  /** Back from Stripe, still TestMode, no recorded failure: the webhook has not landed yet. */
  upgradePending: boolean;
  upgradeFailed: boolean;
  /** Access stage, business verification, Beta Live usage, threshold, contact name. */
  access: ClientAccessView;
}

export interface CheckoutSessionResponse {
  success: boolean;
  error: string | null;
  /** Absolute URL. Assign to window.location - do not fetch it. */
  checkoutUrl: string | null;
}

export interface SimpleOperationResponse {
  success: boolean;
  error: string | null;
}

// -- API keys ------------------------------------------------------------------

export interface ApiKeyListItem {
  id: number;
  name: string;
  /** First 12 characters. The rest exists only as a hash. */
  keyPrefix: string;
  /** "Test" | "Production" */
  environment: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface CreateApiKeyRequest {
  name: string;
  environment: string;
}

export interface ApiKeyOperationResponse {
  success: boolean;
  error: string | null;
  /** The plaintext secret, returned once and never again. Null for revoke and on failure. */
  fullKey: string | null;
  keyName: string | null;
}

// -- Notification preferences --------------------------------------------------

/** One notification group's "Send to". At most 5 addresses, any domain (Core NotificationRecipientRules). */
export type NotificationRecipientMode = 'allAdmins' | 'adminsAndAddresses' | 'addressesOnly';

export interface NotificationSendTo {
  mode: NotificationRecipientMode;
  addresses: string[];
}

export interface NotificationPreferences {
  // Account activity
  accountLocked: boolean;
  accountStatusChanged: boolean;
  planChanged: boolean;
  liveAccessUpdates: boolean;
  // Prepaid credit ("Credit failed" is paymentFailed)
  paymentFailed: boolean;
  creditsLow: boolean;
  creditsExhausted: boolean;
  creditPurchased: boolean;
  autoTopUpFailed: boolean;
  // API keys
  apiKeyAdded: boolean;
  apiKeyRotatedOrRevoked: boolean;
  // Support: copy the other Client Admins on the team's replies. Off by default.
  supportRepliesToAdmins: boolean;
  // Who receives each group ("Send to"); Support has none.
  accountActivitySendTo: NotificationSendTo;
  prepaidCreditSendTo: NotificationSendTo;
  apiKeysSendTo: NotificationSendTo;
  /** Read-only: who "All admins" is (the company contact first, then the active Client Admins). */
  companyRecipients: string[];
}

// -- Integration (customer return URL) -------------------------------------------

/** What the Integration forms send. Rules in IdVerify.Core (ReturnUrls, WebhookUrls). */
export interface IntegrationSettingsInput {
  /** Used when a session has no redirectUrl of its own. null = keep customers on the result screen. */
  defaultRedirectUrl: string | null;
  /**
   * Origins a session's redirectUrl may use, besides the default's. Exact unless written
   * "https://*.host" (any subdomain, not host itself). Empty + no default = unrestricted.
   */
  allowedRedirectOrigins: string[];
  /** null = webhook off (and its secret deleted). */
  webhookUrl: string | null;
  /**
   * Lowest age an age-verification session can check for (1-150). A session asking for less is
   * raised to this. null = no floor.
   */
  minimumAge: number | null;
  /**
   * Steps an age-verification session runs when it doesn't send its own. At least one ID side
   * ("id-front" / "id-back") is required: the date of birth can only come from the document.
   */
  ageVerificationSteps: AgeVerificationStep[];
}

export type AgeVerificationStep = 'liveness' | 'id-front' | 'id-back' | 'face-match';

/** Mirrors portal.api IntegrationSettings. */
export interface IntegrationSettings extends IntegrationSettingsInput {
  webhookSecretSet: boolean;
  /** Last 4 characters of the signing secret. */
  webhookSecretHint: string | null;
  /** This environment accepts http/localhost webhook URLs (development only). */
  allowPrivateWebhookTargets: boolean;
  /** Only on the response that created it — shown once, never returned again. */
  newWebhookSecret: string | null;
}

// -- Platform settings (SuperAdmin) --------------------------------------------

export interface BillingSettings {
  priceAgeCheck: number;
  priceIdExtract: number;
  priceIdVerification: number;
  priceFullKyc: number;
  prepaidDepositAmount: number;
  testModeDailyVerificationLimit: number;
  tryExtractorDailyLimit: number;
  /** Beta Live: free live verifications per UTC day (per-client override on the client page). */
  betaDailyLimit: number;
  /** Beta Live length from approval, in days. */
  betaDurationDays: number;
  /** Platform default auto-decline threshold, 0.50–0.99 (core-field confidence). */
  declineThreshold: number;
  minimumCreditPurchase: number;
  maximumCreditPurchase: number;
  defaultAutoTopUpAmount: number;
  lowBalanceWarningThreshold: number;
  freeTrialTransactions: number;
  maxAutoTopUpAttempts: number;
}

export interface EmailSettings {
  emailNotificationsEnabled: boolean;
  /** Read-only, from Email:Provider in configuration. Ignored when saving. */
  emailProvider: string;
  adminCopyEmail: string | null;
}

export interface SecuritySettings {
  /** Every user must have two-step sign-in on, and cannot turn it off. */
  requireTwoFactor: boolean;
}

/** Platform minimum face-match lines; clients may raise their own, never lower. */
export interface FaceMatchSettings {
  /** SFace cosine similarity, 0.20–0.90. */
  sFaceMinimum: number;
  /** face-api score, 0.50–0.95. */
  neuralMinimum: number;
}

export interface PlatformSettingsResponse {
  billing: BillingSettings;
  email: EmailSettings;
  security: SecuritySettings;
  faceMatch: FaceMatchSettings;
  updatedByEmail: string | null;
  updatedAt: string;
}

/** One decision rule (Admin → Settings → Decision rules). */
export interface DecisionRule {
  code: string;
  label: string;
  /** "Document" (hard rules) or "Fraud". */
  group: string;
  severity: string;
  description: string;
  defaultDeclines: boolean;
  /** True: a verification raising it is declined. False: alert only. */
  declines: boolean;
}

/** One rule on a client's Fraud Rules page: the platform's switch and the client's own. */
export interface ClientDecisionRule {
  code: string;
  label: string;
  /** "Document" (hard rules) or "Fraud". */
  group: string;
  severity: string;
  description: string;
  /** What the platform's Decision rules say for every client. */
  platformDeclines: boolean;
  /** What this client's verifications are decided by. */
  declines: boolean;
  /** The client set its own switch (it differs from the platform's). */
  overridden: boolean;
}

// -- Plans (SuperAdmin) --------------------------------------------------------

export interface PlanListItem {
  id: number;
  tier: string;
  displayName: string;
  rateLimitPerMinute: number;
  isPopular: boolean;
  isActive: boolean;
  sortOrder: number;
  features: string[];
  /** Custom plan only: the default service prices a client moved onto Custom starts from. */
  defaultPrices: ServicePrices | null;
  /** The platform rates (Admin → Settings), shown beside the Custom defaults for comparison. */
  platformPrices: ServicePrices;
  /** Clients on this plan's tier (PlanClientCounts, Core). */
  clientCount: number;
}

export interface UpdatePlanRequest {
  displayName: string;
  rateLimitPerMinute: number;
  isPopular: boolean;
  isActive: boolean;
  sortOrder: number;
  features: string[];
  /** Custom plan only; ignored for other plans. */
  defaultPrices?: ServicePrices;
}

// -- Clients (admin) -----------------------------------------------------------

export interface ClientListItem {
  id: number;
  clientName: string;
  contactEmail: string;
  industry: string;
  tier: string;
  status: string;
  totalVerifications: number;
  createdAt: string;
  /** Business verification waiting for review. */
  kybPending: boolean;
  /** NotSubmitted, Pending, Approved or Rejected. */
  kybStatus: string;
  kybSubmittedAt?: string | null;
  kybReviewedAt?: string | null;
  kybReviewedBy?: string | null;
  /** When a pending request approves itself if nobody reviews it. */
  autoApprovesAt?: string | null;
  // What the client wrote in the request - enough to decide from the list.
  companyName?: string | null;
  businessWebsite?: string | null;
  intendedUse?: string | null;
  projectedMonthlyTransactions?: number | null;
}

/** The client list's live access filter (Core ClientAccess.AccessFilter). */
export type ClientAccessFilter = '' | 'Pending' | 'Approved' | 'Rejected';

export interface ClientListResponse {
  clients: ClientListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** Live access requests waiting for review across all clients, regardless of filters. */
  pendingRequestCount: number;
}

/**
 * What the caller may do on this client, decided server-side. The token carries the role, but
 * the API sends the decisions so the two portals cannot disagree about who may change a plan or
 * move a balance.
 */
export interface ClientDetailPermissions {
  canChangeStatus: boolean;
  canChangePlan: boolean;
  canAdjustCredit: boolean;
  canManageCard: boolean;
  canManageApiKeys: boolean;
  canEdit: boolean;
  canUpgrade: boolean;
  /** Approve / reject business verification, set Beta Live limits. SuperAdmin. */
  canReviewKyb: boolean;
  /** View / change return URLs and webhook (/api/clients/{id}/integration). SuperAdmin. */
  canManageIntegration: boolean;
  /** View / change which notification emails the client receives (/api/clients/{id}/notifications). SuperAdmin. */
  canManageNotifications: boolean;
}

export interface ClientApiKey {
  id: number;
  name: string;
  keyPrefix: string;
  environment: string;
  isActive: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface ClientSession {
  id: number;
  sessionToken: string;
  /** The name, or a stand-in before the ID is read (ApplicantLabel, Core). */
  applicantName: string;
  /** False when applicantName is a stand-in; shown muted. */
  applicantIsName: boolean;
  verificationType: string;
  status: string;
  overallConfidence: number;
  /** Only the back was scanned (ExtractionMethod BarcodeOnly): confidence shows N/A. */
  barcodeOnly: boolean;
  riskScore: number;
  createdAt: string;
}

export interface ClientUser {
  id: number;
  fullName: string;
  email: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface ClientPlanOption {
  id: number;
  tier: string;
  displayName: string;
  rateLimitPerMinute: number;
  isPopular: boolean;
}

export interface ClientCreditLedgerEntry {
  id: number;
  createdAt: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  verificationSessionId: number | null;
  createdByEmail: string | null;
  isCredit: boolean;
}

export interface ClientDetail {
  id: number;
  clientName: string;
  contactEmail: string;
  contactPhone: string;
  website: string | null;
  industry: string;
  tier: string;
  /** The client's own request rate limit - the plan's, or negotiated on Custom. */
  rateLimitPerMinute: number;
  /** What this client pays per call (its own on Custom, else the platform rates). */
  rates: ServicePrices;
  status: string;
  notes: string | null;
  createdAt: string;

  totalVerifications: number;
  approvedCount: number;
  declinedCount: number;

  billingStatus: string;
  currentMonthTransactions: number;
  currentMonthCost: number;
  prepaidCreditBalance: number;

  isPrepaid: boolean;
  freeTransactionsRemaining: number;
  autoTopUpEnabled: boolean;
  autoTopUpAmount: number;
  autoTopUpThreshold: number;
  failedTopUpAttempts: number;

  cardLast4: string | null;
  cardBrand: string | null;
  cardExpiryMonth: number | null;
  cardExpiryYear: number | null;
  hasCard: boolean;

  apiKeys: ClientApiKey[];
  recentSessions: ClientSession[];
  users: ClientUser[];
  access: ClientAccessView;
  plans: ClientPlanOption[];
  creditLedger: ClientCreditLedgerEntry[];
  permissions: ClientDetailPermissions;
  /** Last manual status change, when not Active. SuperAdmin only; null otherwise. */
  latestStatusChange: ClientStatusChange | null;
}

export interface ClientStatusChange {
  changedAt: string;
  details: string | null;
}

/** Body of POST /api/clients/{id}/status. A reason is required for Suspended and Deactivated. */
export interface ChangeClientStatusRequest {
  status: string;
  reason?: string | null;
}

/** Price per call (USD) for each service — ServicePricing in Core. */
export interface ServicePrices {
  ageCheck: number;
  idExtract: number;
  idVerification: number;
  fullKyc: number;
}

/** Pre-fill for the Custom terms editor: the client's own terms, or the defaults. */
export interface CustomPlanTerms {
  rateLimitPerMinute: number;
  prices: ServicePrices;
  isClientSpecific: boolean;
}

/** Body of POST /api/clients/{id}/plan. rateLimitPerMinute and prices are read for Custom only. */
export interface ChangeClientPlanRequest {
  tier: string;
  rateLimitPerMinute?: number;
  prices?: ServicePrices;
}

// -- Client requests -----------------------------------------------------------

export interface CreateClientRequest {
  clientName: string;
  contactEmail: string;
  contactPhone: string;
  website?: string | null;
  industry: string;
  tier: string;
  notes?: string | null;
  /** Custom only: this client's own rate limit. Defaults to the Custom plan's when omitted. */
  rateLimitPerMinute?: number;
  /** Custom only: this client's rate per service. The platform rates when omitted. */
  prices?: ServicePrices;
}

/** Contact details only. Plan, rate limit and pricing change through changeClientPlan. */
export type UpdateClientRequest = Omit<CreateClientRequest, 'tier' | 'rateLimitPerMinute' | 'prices'>;

export interface ClientOperationResponse {
  success: boolean;
  error?: string | null;
  message?: string | null;
}

export interface ClientCreditOperationResponse {
  success: boolean;
  error?: string | null;
  newBalance: number;
}

/** `fullKey` is the only time the plaintext secret exists - it is never stored. */
export interface ClientApiKeyOperationResponse {
  success: boolean;
  error?: string | null;
  fullKey?: string | null;
  keyName?: string | null;
}

// -- Password management -------------------------------------------------------

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface PasswordOperationResponse {
  success: boolean;
  error?: string | null;
  message?: string | null;
  /** Set after a successful self-service change: the replacement for the now-revoked token. */
  accessToken?: string | null;
  expiresAt?: string | null;
}

// -- My account ----------------------------------------------------------------

export interface MyProfile {
  id: number;
  fullName: string;
  /** Your sign-in address. Read-only here - an administrator changes it. */
  email: string;
  role: string;
  roleDisplayName: string;
  /** Null for a SuperAdmin, who belongs to no client. */
  clientName: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  passwordChangedAt: string | null;
  mustChangePassword: boolean;
}

/** Only a name: role, status and email are not yours to change. */
export interface UpdateMyProfileRequest {
  fullName: string;
}

// -- Self-service signup (anonymous) -------------------------------------------

export interface SignupPlan {
  /** "TestMode", "Standard" (Pay-As-You-Go) or "Custom". */
  tier: string;
  displayName: string;
  features: string[];
  isPopular: boolean;
  priceLabel: string;
}

export interface SignupOptions {
  plans: SignupPlan[];
  testModeDailyLimit: number;
  freeTrialTransactions: number;
  minimumCreditPurchase: number;
  maximumCreditPurchase: number;
  creditPresets: number[];
}

export interface SignupRequest {
  tier: string;
  openingCreditAmount: number;
  clientName: string;
  contactEmail: string;
  contactPhone: string;
  website?: string | null;
  industry: string;
  adminFullName: string;
  adminEmail: string;
  /** This SPA's origin, so Stripe sends the customer back here. */
  returnTo?: string;
}

export interface SignupResponse {
  success: boolean;
  error?: string | null;
  /** Test Mode: active now; the temporary password is in the inbox. */
  free: boolean;
  /** Where the temporary password was sent. */
  sentTo?: string | null;
  /** Paid: navigate the browser here. */
  checkoutUrl?: string | null;
}

export interface SignupCompletion {
  state: 'active' | 'depositFailed' | 'pending' | 'unknown';
  message?: string | null;
}

// -- Live access (Sandbox → Beta Live → Live) ----------------------------------

export type AccessStage = 'Sandbox' | 'BetaLive' | 'BetaEnded' | 'Live';
export type KybStatus = 'NotSubmitted' | 'Pending' | 'Approved' | 'Rejected';

/** Mirrors portal.api ClientAccessView. The rules are in Core (ClientAccess, BusinessVerificationService). */
export interface ClientAccessView {
  stage: AccessStage;
  stageLabel: string;

  kybStatus: KybStatus;
  kybSubmittedAt: string | null;
  kybReviewedAt: string | null;
  kybReviewedBy: string | null;
  kybRejectionReason: string | null;
  /** When a pending request approves itself if nobody reviews it (submitted + 24 h). */
  autoApprovesAt: string | null;
  /** "Your request is under review..." while pending. */
  reviewMessage: string | null;

  businessPhone: string | null;
  intendedUse: string | null;
  projectedMonthlyTransactions: number | null;
  acceptableUseAcceptedAt: string | null;
  /** The request form, pre-filled from the last request or from signup. */
  draft: LiveRequestDraft;

  /** Older requests also carried the fields below. */
  legalBusinessName: string | null;
  doingBusinessAs: string | null;
  businessType: string | null;
  ein: string | null;
  businessEmail: string | null;
  businessWebsite: string | null;
  businessDomain: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;

  betaStartedAt: string | null;
  betaEndsAt: string | null;
  betaDailyLimit: number;
  betaDailyLimitOverride: number | null;
  betaUsedToday: number;

  /** 0–1 */
  declineThreshold: number;
  declineThresholdOverride: number | null;
  platformDeclineThreshold: number;

  /** Face-match lines applied (higher of platform minimum and the client's own). SFace: similarity 0–1. */
  faceMatchSFace: number;
  faceMatchSFaceOverride: number | null;
  platformFaceMatchSFace: number;
  /** face-api: score 0–1. */
  faceMatchNeural: number;
  faceMatchNeuralOverride: number | null;
  platformFaceMatchNeural: number;

  contactName: string | null;
  canGoPaidLive: boolean;

  /** Super admin only. */
  signupIpAddress?: string | null;
  sameIpAccounts?: { id: number; name: string }[] | null;
}

export interface LiveRequestDraft {
  companyName: string;
  companyWebsite: string;
  companyEmail: string;
  companyPhone: string;
  intendedUse: string;
  projectedMonthlyTransactions: number | null;
}

/** POST /api/company/kyb - the live access request. */
export interface KybSubmitRequest {
  companyName: string;
  companyWebsite: string;
  companyEmail: string;
  companyPhone: string;
  intendedUse: string;
  projectedMonthlyTransactions: number | null;
  /** "We will not use the service for any unlawful purpose." Must be true. */
  acceptedAcceptableUse: boolean;
}

export interface OperationMessageResponse {
  success: boolean;
  error?: string | null;
  message?: string | null;
}

// -- Support (FEATURES.md 8e) -----------------------------------------------------

/** Team-only fields are null for client users. */
export interface SupportTicketListItem {
  id: number;
  subject: string;
  category: string;
  categoryLabel: string;
  priority: string;
  /** Effective status: a long-resolved request reads as Closed. */
  status: string;
  statusLabel: string;
  /** "Portal" or "ContactForm". */
  source: string;
  /** The other side has written since this side last opened it. */
  unread: boolean;
  contactName: string;
  createdAt: string;
  lastActivityAt: string;
  clientId: number | null;
  clientName: string | null;
  contactEmail: string | null;
  companyName: string | null;
  assignedToName: string | null;
}

export interface SupportTicketListResponse {
  tickets: SupportTicketListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SupportMessage {
  id: number;
  authorName: string;
  isStaff: boolean;
  isInternalNote: boolean;
  body: string;
  createdAt: string;
  attachments: SupportAttachment[];
}

export interface SupportAttachment {
  id: number;
  fileName: string;
  /** Decided from the bytes on upload. */
  contentType: string;
  sizeBytes: number;
  /** Shown as a thumbnail; everything else is a download link. */
  isImage: boolean;
}

export interface SupportTicketDetail extends SupportTicketListItem {
  verificationSessionId: number | null;
  resolvedAt: string | null;
  messages: SupportMessage[];
  canReply: boolean;
  canClose: boolean;
  autoCloseDays: number;
  assignedToUserId: number | null;
  assignees: { id: number; name: string }[];
}

export interface SupportSummary {
  /** Open, In Progress or Waiting on Client. */
  active: number;
  unread: number;
  /** Team only. */
  unassigned: number;
}

export interface CreateSupportTicketRequest {
  subject: string;
  category: string;
  priority: string;
  message: string;
  verificationSessionId: number | null;
}

/** null leaves a field alone; assignedToUserId 0 unassigns. */
export interface SupportTicketUpdateRequest {
  status?: string | null;
  priority?: string | null;
  assignedToUserId?: number | null;
}

// -- Branding (verify-app and SDK look) -------------------------------------------

export type BrandingTheme = 'dark' | 'light' | 'auto';
export type BrandingRadius = 'square' | 'rounded' | 'pill';

/** What a ClientAdmin (or SuperAdmin) can change. Blank name/accent clears them. */
export interface BrandingInput {
  displayName: string | null;
  /** "#rrggbb"; null = InertiaID's default accent. */
  accentColor: string | null;
  theme: BrandingTheme;
  radius: BrandingRadius;
}

export interface BrandingSettings extends BrandingInput {
  /** Black or white text on the accent (server-computed); null with no accent. */
  accentTextColor: string | null;
  hasLogo: boolean;
  /** Changes whenever the logo does — use it to refresh the preview. */
  logoHash: string | null;
  /** False until branding is first saved: customers see the default look. */
  isCustomised: boolean;
}
